import * as React from 'react'
import { useState } from 'react'

import { Map as MapIcon, RadioIcon, Table } from 'lucide-react'
import { inject, MobXProviderContext, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router'

import ViewMode from '~/client/src/shared/components/ViewSelector/ViewMode'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useResourceRuntimeContext } from '~/client/src/shared/hooks/useResourceRuntimeContext'
import useSensors from '~/client/src/shared/hooks/useSensors'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import {
  getResourceSegment,
  getSearchParam,
  removeFromSearchParam,
  urlHasSearchParam,
} from '~/client/src/shared/utils/urlutils'

import BottomNavigationNavBar from '../../components/BottomNavigationNavBar/BottomNavigationNavBar'
import {
  MobileResource,
  MobileResourceConfig,
} from '../../components/MobileResource'
import MobileView from '../../components/MobileView'
import MobileInitialState from '../../stores/MobileInitialState'
import MobileCommonStore from '../../stores/ui/MobileCommon.store'
import MobileStruxTagCreateDrawer from './MobileStruxTagCreateDrawer'
import MobileStruxTagsEditPanel from './MobileStruxTagsEditPanel'
import MobileStruxTagsMap from './MobileStruxTagsMap'

interface IProps {
  state?: MobileInitialState
  common?: MobileCommonStore
}

const STRUXTAGS_UI_DEFAULTS = buildResourceUiConfig(ResourceTypeId.SENSORS)

// The title passes size="lg", a named token Lucide can't resolve.
const HeaderIcon: React.FC<{ className?: string }> = ({ className }) => (
  <RadioIcon size={28} className={className} />
)

const VIEW_MODES: ViewMode[] = [
  { mode: 'list', Icon: Table, label: Localization.translator.listView },
  { mode: 'map', Icon: MapIcon, label: Localization.translator.mapView },
]

const STRUXTAGS_RESOURCE_CONFIG: Omit<MobileResourceConfig, 'runtimeContext'> =
  {
    resourceTypeId: ResourceTypeId.SENSORS,
    title: Localization.translator.struxTags,
    icon: HeaderIcon,
    useRecords: useSensors,
    initialSortKey: STRUXTAGS_UI_DEFAULTS.defaultSortKey,
    initialSortOrder: STRUXTAGS_UI_DEFAULTS.defaultSortOrder,
    initialGroupByKey: STRUXTAGS_UI_DEFAULTS.defaultGroupingKey,
    countLabel: count => `${count} ${Localization.translator.struxTags}`,
    viewModes: VIEW_MODES,
    noneGroupByValue: STRUXTAGS_UI_DEFAULTS.noneGroupingKey,
    emptyMessage: Localization.translator.noDataAvailable,
  }

const MobileStruxTagsContent: React.FC<{
  projectId?: string
  viewMode: string
  onSelectViewMode: (viewMode: string) => void
}> = ({ projectId, viewMode, onSelectViewMode }) => {
  const [isCreateDrawerShown, setIsCreateDrawerShown] = React.useState(false)
  const stores = React.useContext(MobXProviderContext)
  const common: MobileCommonStore = stores.common
  const mountedPathRef = React.useRef(window.location.pathname)

  const runActionFromURL = React.useCallback(
    (transitionRoute?: string) => {
      if (transitionRoute) {
        try {
          const url = new URL(transitionRoute, window.location.origin)
          if (
            getResourceSegment(url.pathname) !==
            getResourceSegment(mountedPathRef.current)
          )
            return
        } catch {
          return
        }
      }

      if (urlHasSearchParam('action')) {
        const action = getSearchParam('action')
        if (action === 'create' && projectId) {
          setIsCreateDrawerShown(true)
          removeFromSearchParam('action', common.history)
        }
      }
    },
    [projectId, common.history],
  )

  React.useEffect(() => {
    runActionFromURL()
    common.addNavigationListener(runActionFromURL)
    return () => {
      common.removeNavigationListener(runActionFromURL)
    }
  }, [runActionFromURL, common])

  return (
    <>
      <MobileView
        header={
          <MobileResource.Header>
            <MobileResource.Title />
          </MobileResource.Header>
        }
        content={
          <MobileResource.Content>
            <MobileResource.FilterBar>
              <MobileResource.Search />
              <MobileResource.FilterButton />
              <MobileResource.ViewOptions />
              <MobileResource.ViewToggle
                selectedViewMode={viewMode}
                onSelectViewMode={onSelectViewMode}
              />
            </MobileResource.FilterBar>
            {viewMode === 'map' ? (
              <MobileStruxTagsMap />
            ) : (
              <MobileResource.List />
            )}
          </MobileResource.Content>
        }
        footer={
          <BottomNavigationNavBar
            onCreateClicked={
              projectId ? () => setIsCreateDrawerShown(true) : undefined
            }
          />
        }
      />
      <MobileResource.FilterPicker />
      <MobileStruxTagsEditPanel />
      {projectId && (
        <MobileStruxTagCreateDrawer
          isShown={isCreateDrawerShown}
          projectId={projectId}
          onClose={() => setIsCreateDrawerShown(false)}
        />
      )}
    </>
  )
}

const MobileStruxTagsView: React.FC<IProps & RouteComponentProps> = ({
  state,
  common,
}) => {
  const [viewMode, setViewMode] = useState('list')
  const projectId = state?.activeProject?.id

  React.useEffect(() => {
    common.hideNavBar()
  }, [common])

  const runtimeContext = useResourceRuntimeContext()

  const resourceConfig = React.useMemo<MobileResourceConfig>(
    () => ({
      ...STRUXTAGS_RESOURCE_CONFIG,
      runtimeContext,
      resolveLeadingSlot: () =>
        renderResourceSemanticIcon(
          'sensor',
          'tw:block tw:size-full tw:text-muted-foreground',
        ),
    }),
    [runtimeContext],
  )

  return (
    <div className="tw:h-full">
      <MobileResource.Provider config={resourceConfig} state={state}>
        <MobileStruxTagsContent
          projectId={projectId}
          viewMode={viewMode}
          onSelectViewMode={setViewMode}
        />
      </MobileResource.Provider>
    </div>
  )
}

export default withRouter(
  inject('state', 'common')(observer(MobileStruxTagsView)),
)
